import type {
  BalanceLineKind,
  BalanceRegistration,
  BalanceSlot,
  OfflinePaymentBatch,
} from '@sportninja/common/services/teamBalance'
import type {
  BalanceCandidateTableRow,
  BalanceGridCandidateRow,
  BalanceGridRow,
  BalanceSheetRow,
  BalanceTableRow,
} from '../types/balanceGrid'

/**
 * Sep 23 — what a Team Budget row IS: a player's share (an open slot or a filled
 * one), a team official's own budget line, or a team official who has no line yet
 * (a $0 candidate).
 *
 * THIS MODULE IS THE ONLY READER of `slot.kind`, `slot.official` and — to decide
 * occupancy — `slot.player_registration_id`. An official line is occupied (its
 * billing registration is the occupant) but has no player, so "occupied means a
 * player" no longer holds; every rule that needs the difference asks here.
 *
 * Pure: no i18n, no ag-grid. A missing `kind` (an older api2) means `player`.
 */

type SlotKindFields = Pick<BalanceSlot, 'kind'>
type OccupancyFields = Pick<BalanceSlot, 'kind' | 'player_registration_id'>

/** A team official's own budget line. */
export const isOfficialLine = (slot: SlotKindFields): boolean =>
  slot.kind === 'official'

/** A player's share — an open player slot or a filled one. */
export const isPlayerLine = (slot: SlotKindFields): boolean =>
  !isOfficialLine(slot)

/**
 * Someone owes on this line — a player, or an official through their line. What
 * the billing actions (invoice, record cash, view payments) need.
 */
export const isOccupiedLine = (
  slot: Pick<BalanceSlot, 'player_registration_id'>
): boolean => slot.player_registration_id !== null

/** A player slot still to fill: Delete, Player Finder, "Open slots", "Awaiting player". */
export const isOpenPlayerSlot = (slot: OccupancyFields): boolean =>
  isPlayerLine(slot) && slot.player_registration_id === null

/** A player slot a player holds: Edit Player, and the footer's "n of m filled". */
export const isFilledPlayerSlot = (slot: OccupancyFields): boolean =>
  isPlayerLine(slot) && slot.player_registration_id !== null

/**
 * A line worth chasing: invoiced, occupied, not cancelled, still owing — a player
 * or an official alike. The SAME rule the server's `remindableSlots()` applies.
 */
export const isRemindableSlot = (
  slot: Pick<
    BalanceSlot,
    'locked_at' | 'player_registration_id' | 'status' | 'owed'
  >
): boolean =>
  slot.locked_at !== null &&
  isOccupiedLine(slot) &&
  slot.status !== 'cancelled' &&
  Number(slot.owed) > 0

/**
 * An official line whose person still qualifies (a Team Rep, the registering rep,
 * a Daysmart manager). A former official's line keeps its history and its debt
 * but takes no new money (D5).
 */
export const isCurrentOfficialLine = (
  slot: Pick<BalanceSlot, 'kind' | 'official'>
): boolean => isOfficialLine(slot) && slot.official?.is_current === true

/**
 * May this line take NEW money — an invoice, a raise, an added invoice? Every
 * player's share may; an official's line only while its person is current (D5).
 */
export const takesNewMoney = (
  slot: Pick<BalanceSlot, 'kind' | 'official'>
): boolean => isPlayerLine(slot) || isCurrentOfficialLine(slot)

/**
 * Ready to be invoiced ("Send Invoice", its checkbox, "Send N Invoices"): pending,
 * and — for an official — still current and above $0. A former official's line and
 * a line kept at $0 for its history are never offered (F3); the server refuses them
 * either way (`official_not_invoiceable`).
 */
export const isInvoiceableLine = (
  slot: Pick<BalanceSlot, 'kind' | 'official' | 'status' | 'amount'>
): boolean =>
  slot.status === 'pending' &&
  (isPlayerLine(slot) ||
    (isCurrentOfficialLine(slot) && Number(slot.amount) > 0))

/**
 * The viewer pays this official line and is not a league admin: they may raise it,
 * never lower, cancel or remove it (D11), and never verify cash on it (D8).
 */
export const isViewerRestricted = (
  slot: Pick<BalanceSlot, 'kind' | 'official'>
): boolean => isOfficialLine(slot) && slot.official?.viewer_restricted === true

/**
 * The `expected_kind` a money write on this row sends (R2-4): `official` for an
 * official's line, nothing for a player's share — exactly what a player write
 * sent before official lines existed.
 */
export const writeKindFor = (
  slot: SlotKindFields
): BalanceLineKind | undefined =>
  isOfficialLine(slot) ? 'official' : undefined

/**
 * The `expected_kind` verifying or voiding an offline batch sends: the batch's OWN
 * `line_kind` (the line it was recorded on) — never the row it happens to sit under,
 * the actor, or its name.
 */
export const batchWriteKind = (
  batch: Pick<OfflinePaymentBatch, 'line_kind'>
): BalanceLineKind | undefined =>
  batch.line_kind === 'official' ? 'official' : undefined

/** The person on a line: the official's name, or the player's. Null for an open slot. */
export const lineName = (
  slot: Pick<BalanceSlot, 'kind' | 'official' | 'player'>
): string | null =>
  isOfficialLine(slot)
    ? slot.official?.name ?? null
    : slot.player?.name_full ?? null

/**
 * The name where it has to stand alone — recipients, the batch list, the payments
 * drawer: "Jeff Day · Team Rep" for an official, so they read apart from Jeff Day
 * the player.
 *
 * Sep 24 — the suffix is the row's TYPE, the same word as its pill, never the
 * roster role (a Daysmart Manager is a Team Rep here too). The caller passes the
 * translated word so this module stays i18n-free.
 */
export const lineLabel = (
  slot: Pick<BalanceSlot, 'kind' | 'official' | 'player'>,
  teamRepLabel: string
): string | null => {
  const name = lineName(slot)
  if (name === null) {
    return null
  }

  return isOfficialLine(slot) ? `${name} · ${teamRepLabel}` : name
}

/** A candidate row on the sheet (a team official with no line yet). */
export const isCandidateTableRow = (
  row: BalanceSheetRow
): row is BalanceCandidateTableRow => 'candidate' in row

/** A slot row on the sheet (a player's share or an official's line). */
export const isSlotTableRow = (row: BalanceSheetRow): row is BalanceTableRow =>
  'slot' in row

/** The same test on the grid's own row shape. */
export const isCandidateRow = (
  row: BalanceGridRow | undefined | null
): row is BalanceGridCandidateRow => row?.kind === 'candidate'

/**
 * A candidate's row key. Namespaced so it can never equal a slot id, and carries the
 * registration so the same person on two divisions (All Divisions) is two rows.
 */
export const candidateRowKey = (
  registrationId: string,
  payerUserId: string
): string => `candidate:${registrationId}:${payerUserId}`

/** The name a sheet row sorts and filters by. */
export const sheetRowName = (row: BalanceSheetRow): string | null =>
  isCandidateTableRow(row) ? row.candidate.name : lineName(row.slot)

/**
 * Jeff, Sep 24 (D5) — WHO a grid row is, for keyboard focus: one key per person.
 *
 * Typing an amount on a $0 candidate opens their line, and $0 on a line with no money
 * history removes it and makes them a candidate again (D6). Under the join order the
 * row then moves, from below the open slots into the joined rows or back, and focus
 * has to move with the person instead of staying on the old row index. So a live
 * official line with a known payer answers its person's candidate key.
 *
 * A cancelled line is history beside that person's live line or candidate, so it
 * keeps its own row key, as do a line with no payer and every player's row. The
 * totals row is nobody. Row persistence (`getRowId`) is untouched: this is only what
 * focus follows.
 */
export const focusIdentityOf = (
  row: BalanceGridRow | undefined | null
): string | null => {
  if (!row || row.kind === 'totals') {
    return null
  }
  if (row.kind === 'candidate') {
    return candidateRowKey(row.registration.id, row.candidate.payer_user_id)
  }
  const payer = row.slot.official?.payer_user_id
  if (isOfficialLine(row.slot) && row.slot.status !== 'cancelled' && payer) {
    return candidateRowKey(row.registration.id, payer)
  }

  return row.key
}

/**
 * The people who already have an ACTIVE line on this registration — a candidate for
 * any of them is a stale duplicate. A cancelled line (the team was cancelled, then
 * reapproved) is history: its person may have one new line beside it.
 */
export const activeOfficialPayers = (
  registration: Pick<BalanceRegistration, 'slots'>
): Set<string> =>
  new Set(
    registration.slots
      .filter(
        (slot) =>
          isOfficialLine(slot) &&
          slot.status !== 'cancelled' &&
          Boolean(slot.official?.payer_user_id)
      )
      .map((slot) => slot.official?.payer_user_id as string)
  )

/**
 * Sep 24 (B-8) — the sheet has no player shares yet, so the team's first-run set-up
 * (how many players) still applies. An official's own line does not set the sheet
 * up: a rep who paid toward the deposit at registration leaves a sheet holding only
 * their line. With no official lines this is exactly "no slots at all", as before.
 */
export const hasNoPlayerLines = (
  registration: Pick<BalanceRegistration, 'slots'>
): boolean => !registration.slots.some(isPlayerLine)

/**
 * The `line_kind` of a batch found anywhere on the sheet — for the one path that
 * knows only a batch id (the verify deep link). Undefined when the sheet has no
 * such batch, which sends no kind; an official batch then answers 409.
 */
export const batchWriteKindOnSheet = (
  registrations: readonly BalanceRegistration[],
  batchId: string
): BalanceLineKind | undefined => {
  for (const registration of registrations) {
    const batches = [
      ...registration.slots.flatMap((slot) => slot.offline_payments ?? []),
      ...(registration.offline_payment_history ?? []),
    ]
    const batch = batches.find((candidate) => candidate.id === batchId)
    if (batch) {
      return batchWriteKind(batch)
    }
  }

  return undefined
}
